import React from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { PageHeader } from './PageHeader';
import { Toaster } from 'sonner';

/**
 * PageShell - The Canonical Root Component for all Feature Pages.
 * 
 * Enforces:
 * 1. Scroll Ownership: Content scrolls within the shell, Window scroll is disabled.
 * 2. Header Sovereignty: Uniform Title/Description/Actions layout.
 * 3. Spacing Rhythm: Fixed container padding (p-8) and gap.
 * 
 * @param {string} title - Page Title (Title Case Enforced by Gate).
 * @param {string} [description] - Page Description.
 * @param {React.ReactNode} [actions] - Primary/Secondary Actions.
 * @param {React.ReactNode} [badge] - Optional Status Badge.
 * @param {React.ReactNode} [filterBar] - Optional Search/Filter Bar.
 * @param {React.ReactNode} children - Page Content.
 */
export function PageShell({
    title,
    description,
    actions,
    badge,
    filterBar,
    children
}) {
    return (
        <div className="flex flex-col h-screen bg-canvas-base overflow-hidden">
            {/* 1. Rigid Header Area (Non-Scrolling) */}
            <div className="flex-none p-8 pb-4 border-b border-border/40 bg-canvas-base z-10">
                <div className="container mx-auto">
                    <PageHeader
                        title={title}
                        description={description}
                        actions={actions}
                        badge={badge}
                        className="mb-0" // Override PageHeader default
                    />
                    {filterBar && (
                        <div className="mt-6">
                            {filterBar}
                        </div>
                    )}
                </div>
            </div>

            {/* 2. Scrollable Content Area (Shell Owns Scroll) */}
            <div className="flex-1 min-h-0 relative">
                <ScrollArea className="h-full w-full">
                    <div className="container mx-auto p-8 pt-6 pb-20">
                        {children}
                    </div>
                </ScrollArea>
            </div>

            {/* 3. Global Toast Anchor */}
            <Toaster />
        </div>
    );
}
